import React from 'react';
import { styled } from '@compiled/react';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';

import AppSwitcherIcon from '@atlaskit/icon/core/app-switcher';
import AppsIcon from '@atlaskit/icon/core/apps';
import CreditCardIcon from '@atlaskit/icon/core/credit-card';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import LegacyActivityIcon from '@atlaskit/icon/core/migration/dashboard--activity';
import LegacyAddonIcon from '@atlaskit/icon/core/migration/app--addon';
import LegacyBillingIcon from '@atlaskit/icon/glyph/billing';
import LegacyFolderIcon from '@atlaskit/icon/core/migration/folder-closed--folder';
import LegacyIssuesIcon from '@atlaskit/icon/core/migration/work-items--issues';
import LegacyPeopleGroupIcon from '@atlaskit/icon/core/migration/people-group';
import LegacyScreenIcon from '@atlaskit/icon/core/migration/screen';
import LegacyShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import ProjectIcon from '@atlaskit/icon/core/project';
import ScreenIcon from '@atlaskit/icon/core/screen';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { getEmceeDiscoverLink } from '@atlassian/jira-platform-marketplace-url-resolver/src/index.tsx';
import { isFedRamp } from '@atlassian/atl-context';
import { isMacOs } from '@atlassian/jira-common-components-keyboard-shortcuts-dialog/src/utils.tsx';
import { manageAppsRoute } from '@atlassian/jira-router-routes-legacy-global-settings-apps-routes/src/manageAppsRoute.tsx';
import type { NavigationItem } from '@atlassian/jira-navigation-types/src/types.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { FilledLozenge } from '@atlassian/jira-command-palette/src/common/ui/custom-lozenge/index.tsx';
import type { ApplicationPermissions } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import messages from './messages.tsx';
import {
	ADMIN_SETTINGS_ITEM_IDS,
	JIRA_SETTINGS_SECTION_IDS,
	SITE_ADMIN_SETTINGS_ITEM_HREFS,
	SITE_ADMIN_SETTINGS_ITEM_IDS,
} from './constants.tsx';

const getApplicationsMenuLink = (appPermissions: ApplicationPermissions): string => {
	if (appPermissions.hasSoftwareAccess)
		return '/jira/settings/products/jira-software-configuration';
	if (appPermissions.hasServiceDeskAccess)
		return '/jira/settings/products/jira-service-management-configuration';

	return '/plugins/servlet/applinks/listApplicationLinks';
};

export const renderScreenIcon = () => (
	<IconWrapper>
		{}
		<LegacyScreenIcon label="" LEGACY_size="small" color={token('color.icon.inverse')} />
	</IconWrapper>
);

export const renderActivityIcon = () => (
	<IconWrapper>
		{}
		<LegacyActivityIcon label="" LEGACY_size="small" color={token('color.icon.inverse')} />
	</IconWrapper>
);

export const renderFolderIcon = () => (
	<IconWrapper>
		{}
		<LegacyFolderIcon label="" LEGACY_size="small" color={token('color.icon.inverse')} />
	</IconWrapper>
);

export const renderIssuesIcon = () => (
	<IconWrapper>
		{}
		<LegacyIssuesIcon label="" LEGACY_size="small" color={token('color.icon.inverse')} />
	</IconWrapper>
);

export const renderAddonIcon = () => (
	<IconWrapper>
		{}
		<LegacyAddonIcon label="" LEGACY_size="small" color={token('color.icon.inverse')} />
	</IconWrapper>
);

export const renderPeopleGroupIcon = () => (
	<IconWrapper dark>
		{}
		<LegacyPeopleGroupIcon label="" LEGACY_size="small" color={token('color.icon.inverse')} />
	</IconWrapper>
);

export const renderBillingIcon = () => (
	<IconWrapper dark>
		<CreditCardIcon
			label=""
			LEGACY_size="small"
			LEGACY_fallbackIcon={LegacyBillingIcon}
			color={token('color.icon.inverse')}
		/>
	</IconWrapper>
);

const getAdminSectionOld = (
	formatMessage: FormatMessage,
	appPermissions: ApplicationPermissions,
): NavigationItem => ({
	id: JIRA_SETTINGS_SECTION_IDS.SYSTEM_ADMIN,
	type: 'Group',
	hasSeparator: true,
	...(expVal('improve_visibility_of_command_palette_for_admins', 'isEnabled', false)
		? {}
		: {
				sideHeading: () => (
					<JiraCommandPaletteTipContainer
						aria-label={formatMessage(
							isMacOs()
								? messages.jiraSettingsCommandPaletteMacScreenReader
								: messages.jiraSettingsCommandPaletteOtherScreenReader,
						)}
					>
						{formatMessage(
							isMacOs()
								? messages.jiraSettingsCommandPaletteMac
								: messages.jiraSettingsCommandPaletteOther,
							{
								strong: (chunks: string) => (
									<Text as="strong" size="small" color="inherit">
										{chunks}
									</Text>
								),
								wrapper: (chunks: React.ReactNode[]) => (
									<CommandPaletteShortcutsContainer>{chunks}</CommandPaletteShortcutsContainer>
								),
								key: (chunks: string) => <KeyboardShortcut>{chunks}</KeyboardShortcut>,
								command: (chunks: string) => <KeyboardShortcut isMeta>{chunks}</KeyboardShortcut>,
							},
						)}
					</JiraCommandPaletteTipContainer>
				),
			}),
	items: [
		{
			goTo: 'root/global-settings/system', // sets active navigation 2nd level menu and an active item in navigation menu, via `navApi.setView(goTo)`
			id: ADMIN_SETTINGS_ITEM_IDS.SYSTEM,
			legacyId: 'admin_system_menu',
			href: '/jira/settings/system/general-configuration',
			text: formatMessage(messages.system),
			type: 'Item',
			before: renderScreenIcon,
		},
		{
			goTo: 'root/global-settings/applications',
			id: ADMIN_SETTINGS_ITEM_IDS.APPLICATIONS,
			legacyId: 'admin_applications_menu',
			href: getApplicationsMenuLink(appPermissions),
			text: formatMessage(messages.applications),
			type: 'Item',
			before: renderActivityIcon,
		},
		{
			goTo: 'root/global-settings/projects',
			id: ADMIN_SETTINGS_ITEM_IDS.MANAGE_PROJECTS,
			legacyId: 'admin_project_menu',
			href: '/jira/settings/projects/manage',
			text: formatMessage(messages.projects),
			type: 'Item',
			before: renderFolderIcon,
		},
		{
			goTo: 'root/global-settings/issues',
			id: fg('jira-issue-terminology-refresh-m3')
				? ADMIN_SETTINGS_ITEM_IDS.WORK
				: ADMIN_SETTINGS_ITEM_IDS.ISSUES,
			legacyId: 'admin_issues_menu',
			href: '/jira/settings/issues/issue-types',
			text: formatMessage(
				fg('jira-issue-terminology-refresh-m3') ? messages.issuesIssueTermRefresh : messages.issues,
			),
			type: 'Item',
			before: renderIssuesIcon,
		},
		{
			goTo: 'root/global-settings/addons',
			id: ADMIN_SETTINGS_ITEM_IDS.PLUGINS,
			legacyId: 'admin_plugins_menu',
			href: getEmceeDiscoverLink({ source: 'side_nav_find_new_addons' }),
			text: formatMessage(messages.addons),
			type: 'Item',
			before: renderAddonIcon,
		},
	],
});

const getAdminSectionNew = (
	formatMessage: FormatMessage,
	appPermissions: ApplicationPermissions,
): NavigationItem => ({
	id: JIRA_SETTINGS_SECTION_IDS.SYSTEM_ADMIN,
	type: 'Group',
	hasSeparator: true,
	heading: formatMessage(messages.jiraAdminSettings),
	items: [
		{
			goTo: 'root/global-settings/system', // sets active navigation 2nd level menu and an active item in navigation menu, via `navApi.setView(goTo)`
			id: ADMIN_SETTINGS_ITEM_IDS.SYSTEM,
			legacyId: 'admin_system_menu',
			href: '/jira/settings/system/general-configuration',
			text: formatMessage(messages.system),
			type: 'Item',
			before: () => <ScreenIcon label="" color={token('color.icon')} />,
			description: formatMessage(messages.systemDescription),
		},
		{
			goTo: 'root/global-settings/applications',
			id: ADMIN_SETTINGS_ITEM_IDS.APPLICATIONS,
			legacyId: 'admin_applications_menu',
			href: getApplicationsMenuLink(appPermissions),
			text: formatMessage(messages.applications),
			type: 'Item',
			before: () => <AppSwitcherIcon label="" color={token('color.icon')} />,
			description: formatMessage(messages.productsDescription),
		},
		{
			goTo: 'root/global-settings/projects',
			id: ADMIN_SETTINGS_ITEM_IDS.MANAGE_PROJECTS,
			legacyId: 'admin_project_menu',
			href: '/jira/settings/projects/manage',
			text: formatMessage(messages.projects),
			type: 'Item',
			before: () => <ProjectIcon label="" color={token('color.icon')} />,
			description: formatMessage(messages.projectsDescription),
		},
		{
			goTo: 'root/global-settings/issues',
			id: fg('jira-issue-terminology-refresh-m3')
				? ADMIN_SETTINGS_ITEM_IDS.WORK
				: ADMIN_SETTINGS_ITEM_IDS.ISSUES,
			legacyId: 'admin_issues_menu',
			href: '/jira/settings/issues/issue-types',
			text: formatMessage(
				fg('jira-issue-terminology-refresh-m3') ? messages.issuesIssueTermRefresh : messages.issues,
			),
			type: 'Item',
			before: () => <WorkItemsIcon label="" color={token('color.icon')} />,
			description: formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.issuesDescriptionIssueTermRefresh
					: messages.issuesDescription,
			),
		},
		{
			goTo: 'root/global-settings/addons',
			id: ADMIN_SETTINGS_ITEM_IDS.PLUGINS,
			legacyId: 'admin_plugins_menu',
			href: manageAppsRoute.path,
			text: formatMessage(messages.addons),
			type: 'Item',
			before: () => <AppsIcon label="" color={token('color.icon')} />,
			description: formatMessage(messages.appsDescription),
		},
		{
			id: 'keyboad-shortcut-tip',
			type: 'Content',
			content: expVal('improve_visibility_of_command_palette_for_admins', 'isEnabled', false)
				? undefined
				: () => (
						<Box
							as="li"
							xcss={jiraCommandPalletteTipContainerStyles}
							aria-label={formatMessage(
								isMacOs()
									? messages.jiraSettingsCommandPaletteMacScreenReader
									: messages.jiraSettingsCommandPaletteOtherScreenReader,
							)}
						>
							{formatMessage(
								isMacOs()
									? messages.jiraSettingsCommandPaletteMac
									: messages.jiraSettingsCommandPaletteOther,
								{
									strong: (chunks: string) => <Text size="small">{chunks}</Text>,
									wrapper: (chunks: React.ReactNode[]) => (
										<Box xcss={commandPalleteShortcutStyles}>{chunks}</Box>
									),
									key: (chunks: string) => (
										<Flex alignItems="center" justifyContent="center" xcss={keyboardShortcutStyles}>
											{chunks}
										</Flex>
									),
									command: (chunks: string) => (
										<Flex alignItems="center" justifyContent="center" xcss={keyboardShortcutStyles}>
											{chunks}
										</Flex>
									),
								},
							)}
						</Box>
					),
		},
	],
});

export const getSiteAdminSectionNew = (
	formatMessage: FormatMessage,
	isSiteAdmin: boolean,
	canManageUsers?: boolean,
): NavigationItem => ({
	id: JIRA_SETTINGS_SECTION_IDS.SITE_ADMIN,
	heading: formatMessage(messages.atlassianAdminSettings),
	type: 'Group',
	hasSeparator: false,
	items: [
		...(canManageUsers || isSiteAdmin
			? [
					{
						id: SITE_ADMIN_SETTINGS_ITEM_IDS.USER_MANAGEMENT,
						legacyId: 'user_management_section',
						href: SITE_ADMIN_SETTINGS_ITEM_HREFS.USER_MANAGEMENT,
						text: formatMessage(messages.userManagement),
						type: 'Item',
						before: () => <TeamsIcon label="" color={token('color.icon')} />,
						after: () => <LinkExternalIcon label="" color={token('color.icon')} />,
						target: '_blank',
						description: formatMessage(messages.userManagementDescription),
					},
				]
			: []),
		...(isSiteAdmin && !isFedRamp()
			? [
					{
						id: SITE_ADMIN_SETTINGS_ITEM_IDS.BILLING,
						legacyId: 'admin_billing_section',
						href: SITE_ADMIN_SETTINGS_ITEM_HREFS.BILLING,
						text: formatMessage(messages.billing),
						type: 'Item',
						before: () => <CreditCardIcon label="" color={token('color.icon')} />,
						after: () => <LinkExternalIcon label="" color={token('color.icon')} />,
						target: '_blank',
						description: formatMessage(messages.billingDescription),
					},
				]
			: []),
	],
});
export const getSiteAdminSectionOld = (
	formatMessage: FormatMessage,
	isSiteAdmin: boolean,
	canManageUsers?: boolean,
): NavigationItem => ({
	id: JIRA_SETTINGS_SECTION_IDS.SITE_ADMIN,
	type: 'Group',
	hasSeparator: false,
	items: [
		...(canManageUsers || isSiteAdmin
			? [
					{
						id: SITE_ADMIN_SETTINGS_ITEM_IDS.USER_MANAGEMENT,
						legacyId: 'user_management_section',
						href: SITE_ADMIN_SETTINGS_ITEM_HREFS.USER_MANAGEMENT,
						text: formatMessage(messages.userManagement),
						type: 'Item',
						before: renderPeopleGroupIcon,
						// @ts-expect-error - TS2769 - No overload matches this call.
						after: () => <LegacyShortcutIcon LEGACY_size="small" />,
						target: '_blank',
					},
				]
			: []),
		...(isSiteAdmin && !isFedRamp()
			? [
					{
						id: SITE_ADMIN_SETTINGS_ITEM_IDS.BILLING,
						legacyId: 'admin_billing_section',
						href: SITE_ADMIN_SETTINGS_ITEM_HREFS.BILLING,
						text: formatMessage(messages.billing),
						type: 'Item',
						before: renderBillingIcon,
						// @ts-expect-error - TS2769 - No overload matches this call.
						after: () => <LegacyShortcutIcon LEGACY_size="small" />,
						target: '_blank',
					},
				]
			: []),
	],
});

export const getOpsAdminSectionOld = (formatMessage: FormatMessage) => ({
	id: JIRA_SETTINGS_SECTION_IDS.OPS_ADMIN,
	type: 'Group',
	hasSeparator: false,
	items: [
		{
			goTo: 'root/global-settings/applications',
			id: ADMIN_SETTINGS_ITEM_IDS.APPLICATIONS,
			legacyId: 'admin_applications_menu',
			href: '/jira/settings/products/ops/role-assignment',
			text: formatMessage(messages.applications),
			type: 'Item',
			before: () => <AppSwitcherIcon label="" color={token('color.icon')} />,
			description: formatMessage(messages.productsDescription),
		},
	],
});

type UserRoles = {
	isAdmin: boolean;
	isSiteAdmin: boolean;
	manageUsersPermission: {
		loading: boolean;
		canManageUsers: boolean | undefined;
	};
	isOpsAdmin?: boolean;
};

export const getGlobalSettingsAdminItems = (
	formatMessage: FormatMessage,
	appPermissions: ApplicationPermissions,
	userRoles: UserRoles,
	willShowNav4: boolean,
): NavigationItem[] => {
	const { isAdmin, isSiteAdmin, manageUsersPermission } = userRoles;
	const result: NavigationItem[] = [];

	if (isAdmin) {
		const getAdminSection = willShowNav4 ? getAdminSectionNew : getAdminSectionOld;

		result.push(getAdminSection(formatMessage, appPermissions));
	}

	// Adding these items to the end because `manageUsersPermission` may change after the initial
	// render because it is determined by an async API call. It will need to be the responsibility
	// of the consumer to show a loading state if needed.
	if (
		isSiteAdmin ||
		(manageUsersPermission.loading === false && manageUsersPermission.canManageUsers)
	) {
		const getSiteAdminSection = willShowNav4 ? getSiteAdminSectionNew : getSiteAdminSectionOld;

		result.push(
			getSiteAdminSection(
				formatMessage,
				userRoles.isSiteAdmin,
				manageUsersPermission.canManageUsers,
			),
		);
	}

	if (
		!willShowNav4 &&
		!userRoles.isAdmin &&
		userRoles.isOpsAdmin &&
		fg('operations-tenant-info-endpoint-update')
	) {
		result.push(getOpsAdminSectionOld(formatMessage));
	}

	return result;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const IconWrapper = styled.div<{ dark?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) =>
		props.dark ? token('color.background.neutral.bold') : token('color.background.brand.bold'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '24px',
	width: '24px',
	borderRadius: '3px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommandPaletteShortcutsContainer = styled.div({
	display: 'inline-flex',
	gap: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const KeyboardShortcut = styled(FilledLozenge)<{ isMeta?: boolean }>({
	display: 'block',
	width: 'auto',
	minWidth: '14px',
	height: '14px',
	flexGrow: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	lineHeight: '14px !important',
	textAlign: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	fontSize: ({ isMeta }) => (isMeta ? '11px !important' : '8px !important'),
	paddingTop: 0,
	paddingRight: token('space.025'),
	paddingBottom: 0,
	paddingLeft: token('space.025'),
	boxSizing: 'border-box',
	textTransform: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const JiraCommandPaletteTipContainer = styled.div({
	font: token('font.body.small'),
	fontWeight: token('font.weight.regular'),
	color: token('color.text.accent.gray'),
	textTransform: 'none',
});

const commandPalleteShortcutStyles = xcss({
	display: 'inline-flex',
	gap: 'space.025',
});

const jiraCommandPalletteTipContainerStyles = xcss({
	font: token('font.body.small'),
	marginInlineStart: 'space.200',
	marginBlock: 'space.200',
	color: 'color.text.subtle',
	listStyleType: 'none',
});

const keyboardShortcutStyles = xcss({
	minWidth: '14px',
	height: '14px',
	backgroundColor: 'color.background.accent.blue.subtlest',
	color: 'color.text.subtle',
	padding: 'space.025',
	borderRadius: 'border.radius',
	font: token('font.body.small'),
});
